import React, { useEffect, useState, useMemo } from "react";
import { io } from "socket.io-client";
import api from "../../api/api";
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar
} from 'recharts';
import { toast } from "react-toastify";

const SOCKET_URL = "https://imagensdaface.com.br";

export default function RealTimeMonitor() {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [viewMode, setViewMode] = useState("live");
  const [stats, setStats] = useState({ 
    topPages: [], accessHistory: [], recentActivity: [], devices: [], totalUniques: 0 
  });

  // Estados para Controle de Seleção e Paginação
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadDBStats = async () => {
    try {
      const res = await api.get("/admin/analytics/stats");
      if (res.data) setStats(res.data);
    } catch (err) { 
        console.error("Erro ao carregar dados:", err);
    }
  };

  useEffect(() => {
    loadDBStats();
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"], path: "/socket.io/" });
    socket.on("admin_update_users", (users) => {
      setOnlineUsers([...users].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    });
    return () => socket.disconnect();
  }, []);

  // Lógica de Paginação
  const paginatedHistory = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return (stats.recentActivity || []).slice(startIndex, startIndex + itemsPerPage);
  }, [stats.recentActivity, currentPage]);

  const totalPages = Math.ceil((stats.recentActivity?.length || 0) / itemsPerPage) || 1;

  // Lógica de Seleção
  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const pageIds = paginatedHistory.map(u => u.id).filter(Boolean);
    const allSelected = pageIds.every(id => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  // Lógica de Exclusão
  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Excluir ${selectedIds.length} registros permanentemente?`)) return;

    try {
      await api.delete("/admin/analytics/logs", { data: { ids: selectedIds } });
      toast.success("Registros apagados!");
      setSelectedIds([]);
      loadDBStats();
    } catch (err) {
      toast.error("Erro ao excluir dados.");
    }
  };

  const metrics = useMemo(() => {
    const totalClicks = (stats.accessHistory || []).reduce((acc, c) => acc + (Number(c.count) || 0), 0);
    const devices = stats.devices || [];
    const mobile = devices.find(d => d.name === 'Mobile')?.value || 0;
    const totalDev = devices.reduce((a, b) => a + (Number(b.value) || 0), 0);
    const mobileRate = totalDev > 0 ? Math.round((mobile / totalDev) * 100) : 0;
    return { totalClicks, mobileRate };
  }, [stats]);

  return (
    <div className="p-6 space-y-6 bg-[#f8fafc] min-h-screen font-sans text-left">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
        <div className="text-left w-full">
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Painel de Monitoramento</h1>
          <p className="text-slate-500 text-sm font-medium">Controle de tráfego e logs de auditoria</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-xl mt-4 md:mt-0">
          <TabBtn active={viewMode === 'live'} onClick={() => { setViewMode("live"); setSelectedIds([]); }} label={`Online (${onlineUsers.length})`} />
          <TabBtn active={viewMode === 'history'} onClick={() => setViewMode("history")} label="Histórico" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Online Agora" val={onlineUsers.length} color="text-indigo-600" live />
        <KPICard label="Visitantes Únicos" val={stats.totalUniques} color="text-slate-800" />
        <KPICard label="Cliques (24h)" val={metrics.totalClicks} color="text-emerald-600" />
        <KPICard label="Taxa Mobile" val={`${metrics.mobileRate}%`} color="text-rose-500" />
      </div>

      {/* GRÁFICOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
          <h3 className="text-xs font-black text-slate-400 uppercase mb-6 tracking-widest text-left">Fluxo de Acessos (Hora)</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.accessHistory}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
                <Tooltip contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}} />
                <Area type="monotone" dataKey="count" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.05} strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
          <h3 className="text-xs font-black text-slate-400 uppercase mb-6 tracking-widest text-left">Páginas mais Populares</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.topPages} layout="vertical">
                <XAxis type="number" hide />
                <YAxis dataKey="page_url" type="category" width={100} fontSize={10} axisLine={false} tickLine={false} />
                <Tooltip cursor={{fill: '#f8fafc'}} />
                <Bar dataKey="clicks" fill="#6366f1" radius={[0, 10, 10, 0]} barSize={15} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* TABELA DE LOGS */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h3 className="font-black text-slate-800 text-sm uppercase">Detalhamento</h3>
            {selectedIds.length > 0 && viewMode === 'history' && (
              <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase hover:bg-red-700 transition-all">
                Apagar Selecionados ({selectedIds.length})
              </button>
            )}
          </div>
          <button onClick={loadDBStats} className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">Sincronizar Banco</button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <tr>
                <th className="px-6 py-4 w-12 text-center">
                  {viewMode === 'history' && (
                    <input 
                      type="checkbox" 
                      className="cursor-pointer h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      onChange={handleSelectAll} 
                      checked={paginatedHistory.length > 0 && paginatedHistory.every(u => selectedIds.includes(u.id))}
                    />
                  )}
                </th>
                <th className="px-6 py-4">Usuário / IP</th>
                <th className="px-6 py-4">Página / Referência</th>
                <th className="px-6 py-4 text-center">Aparelho</th>
                <th className="px-6 py-4 text-right">Data/Hora</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(viewMode === 'live' ? onlineUsers : paginatedHistory).map((u, i) => {
                const isRowSelected = selectedIds.includes(u.id);
                return (
                  <tr key={u.id || u.socketId || i} className={`transition-all text-xs ${isRowSelected ? 'bg-indigo-50/50' : 'hover:bg-slate-50'}`}>
                    <td className="px-6 py-4 text-center">
                      {viewMode === 'history' && u.id && (
                        <input 
                          type="checkbox" 
                          className="cursor-pointer h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={isRowSelected} 
                          onChange={() => toggleSelect(u.id)} 
                        />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800">{u.userName || u.user_name}</div>
                      <div className="text-[9px] font-mono text-slate-400 bg-slate-100 w-fit px-1 rounded">{u.ip || u.ip_address}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-indigo-600 font-bold truncate max-w-[200px] uppercase tracking-tighter">{u.page || u.page_url}</div>
                      <div className="text-[9px] text-slate-300 truncate max-w-[200px]">{u.ref || u.referrer_url || 'Navegação Direta'}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                       <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${u.device === 'Mobile' || u.device_type === 'Mobile' ? 'bg-rose-50 text-rose-500 border-rose-100' : 'bg-blue-50 text-blue-500 border-blue-100'}`}>
                          {u.device || u.device_type || 'Desktop'}
                       </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-[10px] text-slate-400">
                      <div className="font-bold text-slate-700">{new Date(u.timestamp || u.action_date).toLocaleTimeString()}</div>
                      <div className="opacity-50">{new Date(u.timestamp || u.action_date).toLocaleDateString()}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {viewMode === 'history' && totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 flex justify-center items-center gap-4 bg-slate-50/50">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="px-4 py-1.5 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 disabled:opacity-30 hover:bg-slate-50 transition-all">Anterior</button>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Página {currentPage} de {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="px-4 py-1.5 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 disabled:opacity-30 hover:bg-slate-50 transition-all">Próxima</button>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, label }) {
  return (
    <button onClick={onClick} className={`px-6 py-2 rounded-lg text-xs font-bold transition-all ${active ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-indigo-400'}`}>
      {label}
    </button>
  );
}

function KPICard({ label, val, color, live }) {
  return (
    <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-200 flex flex-col items-start hover:shadow-md transition-shadow">
      <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <h2 className={`text-2xl font-black ${color}`}>{val}</h2>
        {live && <span className="h-2 w-2 rounded-full bg-green-500 animate-ping"></span>}
      </div>
    </div>
  );
}